import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useQuery } from 'react-query';
import { IBuild, IResponse } from '../../../models';
import { getPersonBuilds } from '../../../fetch';
import { UserContext } from '../../../context';

interface BuildsContextType {
  builds: IBuild[];
  selectedBuild: IBuild | null;
  isLoading: boolean;
  error: Error | null;
  isHoaal: boolean;
  setSelectedBuild: (build: IBuild | null) => void;
  // Helper functions for filtering builds
  getBuildsWithContracts: () => IBuild[];
  getBuildsWithSitePlans: () => IBuild[];
  getBuildsWithStatus: (statuses: string[]) => IBuild[];
}

const BuildsContext = createContext<BuildsContextType | undefined>(undefined);

interface BuildsContextHandlerProps {
  children: ReactNode;
}

export const BuildsContextHandler: React.FC<BuildsContextHandlerProps> = ({ children }) => {
  const { user } = useContext(UserContext);
  const personId = user?.externalUser?.personId;
  const [selectedBuild, setSelectedBuild] = useState<IBuild | null>(null);
  const [hasUserSelected, setHasUserSelected] = useState(false);

  const {
    data: buildsResponse,
    isLoading,
    error
  } = useQuery<IResponse<IBuild[]>, Error>(
    ['homeowner-portal-all-builds', personId],
    async () =>
      await getPersonBuilds(personId, {
        perPage: '2147483647', // Get all records
        sortBy: 'buildId',
        sortDirection: 'desc'
      }),
    {
      enabled: !!personId
    }
  );

  const builds = useMemo(() => {
    return buildsResponse?.records || [];
  }, [buildsResponse?.records]);

  const isHoaal = selectedBuild?.isHoaal || false;

  // Auto-select the first build only if user hasn't made a selection and builds are available
  useEffect(() => {
    if (builds.length > 0 && !selectedBuild && !hasUserSelected) {
      setSelectedBuild(builds[0]);
    }
  }, [builds, selectedBuild, hasUserSelected]);

  // Ensure selected build is still valid when builds data changes
  useEffect(() => {
    if (selectedBuild && builds.length > 0) {
      const stillExists = builds.find(build => build.buildId === selectedBuild.buildId);
      if (!stillExists) {
        // If the previously selected build no longer exists, select the first available
        setSelectedBuild(builds[0]);
      }
    }
  }, [builds, selectedBuild]);

  // Helper function to get builds with contracts
  const getBuildsWithContracts = (): IBuild[] => {
    return builds.filter(
      build =>
        ['ContractAccepted', 'TenDayComplete', 'Closing', 'Complete'].includes(build.status) &&
        build.hasContractFile
    );
  };

  // Helper function to get builds with site plans
  const getBuildsWithSitePlans = (): IBuild[] => {
    return builds.filter(build => build.hasSitePlanFile);
  };

  // Helper function to get builds with specific statuses
  const getBuildsWithStatus = (statuses: string[]): IBuild[] => {
    return builds.filter(build => statuses.includes(build.status));
  };

  // Wrapper function to track user selections
  const handleSetSelectedBuild = (build: IBuild | null) => {
    setSelectedBuild(build);
    setHasUserSelected(true);
  };

  const contextValue: BuildsContextType = {
    builds,
    selectedBuild,
    isLoading,
    error,
    isHoaal,
    setSelectedBuild: handleSetSelectedBuild,
    getBuildsWithContracts,
    getBuildsWithSitePlans,
    getBuildsWithStatus
  };

  return <BuildsContext.Provider value={contextValue}>{children}</BuildsContext.Provider>;
};

// Custom hook to use the builds context
export const useBuilds = (): BuildsContextType => {
  const context = useContext(BuildsContext);
  if (context === undefined) {
    throw new Error('useBuilds must be used within a BuildsProvider');
  }
  return context;
};
